/**
 * A arte do campus, guardada FORA do jogo Phaser.
 *
 * ## O defeito que isto fecha
 *
 * O escritório desmonta e volta a montar com frequência: trocar para o chat, o
 * Fast Refresh do Vite a cada ficheiro guardado, mudar de servidor. Cada
 * montagem cria uma cena nova, e cada cena nova voltava a PEDIR ao servidor os
 * ~70 PNG do campus. O `preload` de cada módulo arma uma rede de segurança
 * (`falhadas`) que, perante um `loaderror`, salta o PNG e deixa a arte desenhada
 * em código do v1 tomar a chave — de propósito, porque um 404 no `load.image`
 * não dá erro, mete a textura `__MISSING` e enche o mundo de quadrados pretos.
 *
 * A rede funciona bem demais: é SILENCIOSA. Basta um pedido a falhar — um blip
 * do túnel, o servidor de desenvolvimento ocupado a reconstruir o módulo que
 * acabou de ser guardado, a rede a oscilar — para aquela peça voltar ao desenho
 * geométrico do v1 sem uma linha na consola. O Phaser ainda repete o ficheiro
 * duas vezes (`loaderMaxRetries`), mas **sem qualquer espera entre tentativas**:
 * as três tentativas gastam-se em milissegundos, dentro da mesma falha. Uma
 * intermitência de dois segundos condena a chave para o resto da vida da cena.
 *
 * Daí o sintoma que o Kawã descreveu: *"às vezes, quando estás a modificar um
 * ficheiro, o escritório volta às origens, com gráficos diferentes do
 * original"*. Não é aleatório e não é o código do desenho — é a arte a não
 * chegar, numa remontagem provocada por uma gravação.
 *
 * ## A correcção
 *
 * A imagem já descodificada não tem de morrer com o jogo. Guardamos aqui, por
 * chave, o `HTMLImageElement` de cada PNG que CHEGOU, e na montagem seguinte
 * registamo-lo na cena ANTES dos `preload`. O `LoaderPlugin` do Phaser salta
 * qualquer ficheiro cuja chave já exista no gestor de texturas
 * (`File.hasCacheConflict`), portanto os `scene.load.image(...)` dos quatro
 * módulos passam a não-operações sozinhos — sem um `if` novo em nenhum deles.
 *
 * Consequências, todas desejadas:
 *   - a segunda montagem e as seguintes não fazem UM ÚNICO pedido de arte, logo
 *     não há janela de falha: o escritório não pode voltar às origens;
 *   - o arranque fica instantâneo em vez de esperar por 70 descodificações;
 *   - a cache só CRESCE, e só com o que carregou de verdade. Se a primeira
 *     montagem apanhou uma falha, a peça em falta simplesmente não está aqui,
 *     volta a ser pedida na próxima e, ao chegar, REPARA-SE — a remontagem
 *     passa a curar o escritório em vez de o degradar.
 *
 * O que NÃO guardamos: as texturas feitas em canvas (`addCanvas`) — os recortes
 * dos móveis, os frames do boneco, o desenho do v1. São baratas de refazer e
 * guardá-las era ficar com o recorte velho depois de mexer no `officeFurniture`.
 * Só entram aqui `HTMLImageElement`, ou seja, exactamente os ficheiros.
 *
 * Custo: as imagens ficam em memória enquanto a página viver (~4 MB de PNG). É a
 * mesma memória que o escritório precisa no instante em que se volta a ele, e um
 * F5 liberta-a. Trocar isso por um escritório que nunca se degrada é barato.
 */

import type PhaserT from "phaser";

/** O contrato mínimo de uma cena para esta cache — o que os testes conseguem forjar. */
type CenaComTexturas = Pick<PhaserT.Scene, "textures">;

/**
 * Chave da textura → a imagem descodificada.
 *
 * Vive ao nível do módulo (fora do React e fora do Phaser) e, em
 * desenvolvimento, dentro do `import.meta.hot.data`: o Vite passa esse objecto
 * da instância antiga do módulo para a nova, por isso guardar um ficheiro deixa
 * de esvaziar a cache. Sem isto a correcção não valia nada precisamente no caso
 * que o Kawã relatou — o Fast Refresh.
 *
 * O `?.` no `data` não é decoração: o Vitest define `import.meta.hot` mas não o
 * `data`, e sem ele o próprio módulo rebentava a ser importado num teste. Em
 * produção `hot` é `undefined` e os dois ramos desaparecem no build.
 */
const arte: Map<string, HTMLImageElement> = import.meta.hot?.data?.arteDoCampus ?? new Map();
if (import.meta.hot?.data) import.meta.hot.data.arteDoCampus = arte;

/**
 * Regista na cena tudo o que já está em cache.
 *
 * Tem de correr no TOPO do `preload`, antes dos `preloadOffice*`: é a ordem que
 * faz o loader saltar os ficheiros. Chamado depois já não serve de nada — a
 * fila já foi montada e os pedidos já saíram.
 *
 * @returns quantas chaves foram servidas (0 na primeira montagem da página).
 */
export function servirArteEmCache(scene: CenaComTexturas): number {
  let servidas = 0;
  for (const [chave, imagem] of arte) {
    if (scene.textures.exists(chave)) continue;
    scene.textures.addImage(chave, imagem);
    servidas += 1;
  }
  return servidas;
}

/**
 * Guarda o que a cena carregou de verdade.
 *
 * Tem de correr no INÍCIO do `create` — depois do `preload` (portanto com os
 * ficheiros já em casa) e antes de o mundo ser desenhado (portanto antes de
 * existir uma única textura de canvas para filtrar).
 *
 * A textura `__MISSING` que o Phaser mete no lugar de um 404 nunca entra:
 * `getTextureKeys` exclui as três texturas internas do motor.
 *
 * @returns o total de peças em cache depois desta captura.
 */
export function guardarArteDaCena(scene: CenaComTexturas): number {
  for (const chave of scene.textures.getTextureKeys()) {
    if (arte.has(chave)) continue;
    const fonte = scene.textures.get(chave).getSourceImage();
    // Só ficheiros. Um canvas é arte gerada — refaz-se, não se guarda.
    if (fonte instanceof HTMLImageElement && fonte.complete && fonte.naturalWidth > 0) {
      arte.set(chave, fonte);
    }
  }
  return arte.size;
}

/** Quantas peças de arte estão guardadas. Existe para os testes e para diagnóstico. */
export function arteEmCache(): number {
  return arte.size;
}

/** Esvazia a cache. Só para os testes — em produção a cache é para durar. */
export function limparArteEmCache(): void {
  arte.clear();
}
